import React, { useState } from 'react';
import Error from '@atlaskit/icon/core/status-error';
import styled from 'styled-components';
import Flag from '@atlaskit/flag';
import { EnhancedOrganisation } from 'modules/checkout/contacts/duck/model';
import t from 'modules/i18n/intl';
import { Field, FieldText } from 'modules/form';
import TaxIdRadio from 'modules/checkout/contacts/TaxIdRadio';
import { Dimensions } from 'components/visuals';
import {
  checkIsCountryCanada,
  checkIsCountryEgypt,
  getVATResponsibleCopies,
  taxExemptionsLabels,
} from 'modules/utils';
import { checkFeatureGate, FeatureFlags } from 'util/statsig';

export const Subtext = styled.p`
  font-size: small;
  position: relative;
  top: ${Dimensions.units(-1)}px;
  font-weight: 400;
`;

export const TaxFreeMessage = ({ taxRate, country }: any) => {
  if (taxRate.taxFreeWithValidTaxId) {
    switch (taxRate.taxIdLabel) {
      case 'VAT Number':
        return <Subtext>{t('checkout.contact.tax.vat-added')}</Subtext>;
      case 'ABN':
        return <Subtext>{t('checkout.contact.tax.au-gst-added')}</Subtext>;
      case 'Organization Number':
        return (
          <Subtext>
            {t('checkout.contact.tax.org-no-vat-added', { country: country.countryIsoCode })}
          </Subtext>
        );
      case 'Registration Number':
        return <Subtext>{t('checkout.contact.tax.japanese-consumption-tax-added')}</Subtext>;
      case 'GUI Number':
        return <Subtext>{t('checkout.contact.tax.gui-vat-added')}</Subtext>;
      case 'GSTIN':
        return <Subtext>{t('checkout.contact.tax.gstin-added')}</Subtext>;
      case 'QST number':
        return <Subtext>{t('checkout.contact.tax.qst-added')}</Subtext>;
      default:
        return null;
    }
  }
  return null;
};

export default ({
  taxRate,
  showTaxId,
  taxIdLabel,
  country,
  vatResponsible,
  taxId,
  state,
}: Partial<EnhancedOrganisation>) => {
  const cartCanadaTaxExemptionsFieldFG = checkFeatureGate(
    FeatureFlags.CART_CANADA_TAX_EXEMPTIONS_FIELD,
  );
  const cartSerbiaVatEnabledFG = checkFeatureGate(FeatureFlags.CART_ENABLE_VAT_FOR_SERBIA);

  const [isTaxIdFieldEmpty, setIsTaxIdFieldEmpty] = useState(!taxId);

  return (
    taxRate?.fold({
      success: (rate) => {
        const {
          isCountryChile,
          isCountryPhilippines,
          isCountryThailand,
          isCountrySerbia,
          taxIdPlaceholder,
          VATRequiredMessageId,
          VATNotRequiredMessageId,
        } = getVATResponsibleCopies(country?.isoCountryCode);

        const isCountryEgypt = checkIsCountryEgypt(country?.isoCountryCode);
        const isCountryCanada = checkIsCountryCanada(country?.isoCountryCode);
        const taxIdLabelUpdated = isCountryEgypt ? 'TRN' : taxIdLabel;
        let taxExemptionsLabel = '';

        if (isCountryEgypt) {
          taxExemptionsLabel = taxExemptionsLabels.Egypt;
        } else if (isCountryCanada) {
          taxExemptionsLabel = taxExemptionsLabels.Canada[state || ''];
        }

        return showTaxId ? (
          <>
            <Field
              name="taxId"
              label={taxIdLabelUpdated}
              errorMessage={() =>
                t('checkout.contact.enter-valid-taxid', { taxIdLabel: taxIdLabelUpdated })
              }
            >
              <FieldText
                type="text"
                placeholder={taxIdPlaceholder}
                setIsFieldEmpty={setIsTaxIdFieldEmpty}
              />
            </Field>
            {((isCountryEgypt && !isTaxIdFieldEmpty) ||
              (cartCanadaTaxExemptionsFieldFG &&
                isCountryCanada &&
                ['MB', 'BC', 'QC', 'SK'].includes(state || ''))) && (
              <Field
                name="taxExemptions"
                label={taxExemptionsLabel}
                errorMessage={() =>
                  t('checkout.contact.enter-valid-taxid', { taxIdLabel: taxExemptionsLabel })
                }
                isRequired={isCountryEgypt}
              >
                <FieldText type="text" />
              </Field>
            )}
            <TaxFreeMessage taxRate={rate} country={country} />
            {(isCountryChile ||
              isCountryPhilippines ||
              isCountryThailand ||
              (isCountrySerbia && cartSerbiaVatEnabledFG)) && (
              <Field
                name="vatResponsible"
                errorMessage={() => t('checkout.contact.tax-id-vat-error-message')}
              >
                <TaxIdRadio
                  vatResponsible={vatResponsible}
                  isTaxIdFieldEmpty={isTaxIdFieldEmpty}
                  VATRequiredMessageId={VATRequiredMessageId}
                  VATNotRequiredMessageId={VATNotRequiredMessageId}
                />
              </Field>
            )}
          </>
        ) : null;
      },
      failed: () => (
        <Flag
          id="1"
          appearance="error"
          icon={<Error label="Error icon" />}
          title={t('checkout.contact.unable-to-load-tax-info')}
          description={t('checkout.contact.tax-description')}
        />
      ),
    }) || null
  );
};
